import { contactEmail } from "../env";

export const termsOfServiceMarkdown = `
# Terms of Service – Collective Discovery by Canucci

**Last updated:** 29 November 2025

These Terms of Service (“Terms”) govern access to and use of **Collective Discovery by Canucci**, including Collect, Collective Canvas, Collective Insights, Compose, and any related online services (together, the “Services”).

The Services are provided by:

**Canucci AB**
Org. no. 559266-0004
Box 16066
SE-103 22 Stockholm
Sweden
Email: [${contactEmail()}](mailto:${contactEmail()})

By accessing or using the Services, you agree to these Terms on behalf of the organization you represent. If you do not agree, you must not use the Services.

---

## 1. Contracting parties and scope

1.1 **Business use only**
The Services are intended for use by organizations, such as companies, municipalities, public agencies and non-profits (“Customers”), not for private consumer use.

1.2 **Contracting parties**
These Terms form part of the legal agreement between **Canucci AB** (“Canucci”, “we”, “us”) and the relevant **Customer**. Individual users access the Services as Users of a Customer.

1.3 **Relationship to enterprise agreements**
If Canucci and a Customer have entered into a separate written services agreement, order form or similar (an “Enterprise Agreement”), that Enterprise Agreement governs the commercial terms of the relationship (including fees, term, service levels and liability). In the event of conflict between these Terms and an Enterprise Agreement, the Enterprise Agreement will prevail.

1.4 **Scope of Services**
These Terms apply to the use of Collective Discovery by Canucci, including in particular:

* **Collect** – interview and response collection services;
* **Collective Canvas** – visualization, discussion and follow-up of results;
* **Collective Insights** – analysis and insight tools;
* **Compose** – content creation tools to support communication;
* any other modules or features that Canucci may make available as part of Collective Discovery.

---

## 2. Definitions

For the purposes of these Terms:

* **“Customer”** means the organization (such as a company, municipality, public agency or non-profit) that has entered into an agreement with Canucci to use the Services.
* **“Users”** means all individuals who access the Services in connection with a Customer account, including Admins, Facilitators and Participants.
* **“Admin”** means a User designated by the Customer with permissions to manage the Customer’s account and organization-level settings, including the ability to create or approve projects, manage User roles and set security configurations.
* **“Facilitator”** means a User who runs one or more projects on behalf of the Customer. Facilitators may set up interviews in Collect, work with analysis and insights, configure canvases and present results.
* **“Participant”** means a User who has been invited by the Customer to respond to questions, take part in sessions, or view related canvases (for example, employees, team members, citizens or other respondents). Participants typically interact with Collect and Canvas.
* **“Content”** means any questions, responses, comments, labels, tags, documents, settings, canvases, insights, summaries or other information submitted to or generated in the Services in connection with a Customer’s use of the Services.

---

## 3. Who may use the Services

3.1 **Authority to bind the Customer**
If you create an account, configure projects or otherwise activate the Services using an email address issued by a Customer, you represent and warrant that you are authorized to do so on that Customer’s behalf, and that the Customer agrees to be bound by these Terms.

3.2 **Users under a Customer’s account**
All Admins, Facilitators and Participants use the Services in connection with a Customer account. Participants use the Services only because they have been invited by a Customer. Participants are not parties to any commercial agreement with Canucci.

3.3 **Professional or civic capacity**
By using the Services, you confirm that you are acting in a professional or civic capacity (for example as an employee, manager, civil servant, citizen engaged in a public consultation, or similar), not as a private consumer.

---

## 4. Accounts, access and roles

4.1 **Accounts**
An account is required for Admins and Facilitators, and for Users who access protected canvases. No account is required to respond to interviews in Collect; respondents do not log in when submitting their answers.

4.2 **Role-based access**
The Customer assigns Users to roles (Admin, Facilitator, Participant) and controls their permissions. Canucci may provide defaults and recommendations, but the Customer is responsible for who receives which role and what they can access.

4.3 **Account security**
Users must keep their login credentials confidential and notify the Customer or Canucci promptly of any suspected unauthorized access. The Customer is responsible for any actions taken through its accounts.

---

## 5. Customer responsibilities

5.1 **Overall responsibility for use**
As between Canucci and the Customer, the Customer is responsible for:

* selecting and authorizing its Admins, Facilitators and Participants;
* ensuring that all use of the Services by its Users complies with these Terms and applicable law; and
* any Content submitted or actions taken through its accounts.

5.2 **Content and interview design**
The Customer is responsible for the Content it and its Users submit, including the design of interview questions and projects. In particular, the Customer is responsible for ensuring that:

* questions and projects are lawful and appropriate;
* any internal consultations with works councils, unions, DPOs or other stakeholders are carried out where required; and
* the Services are not used to discriminate against or unfairly target individuals or groups.

5.3 **Acceptable use**
The Customer must ensure that its Users do not:

* upload or share content that is illegal, discriminatory, hateful, defamatory or otherwise unlawful;
* use the Services to harass, threaten or target individuals (including “witch hunts” against colleagues or specific persons);
* attempt to circumvent or disable any technical safeguards in the Services;
* attempt to deanonymise respondents in ways that go beyond the capabilities and configuration provided by the Services;
* use automated systems or scripts to overload or attack the Services, or to scan for vulnerabilities.

Canucci may suspend or restrict access where necessary to protect the Services, other customers or respondents.

---

## 6. Participants and anonymity

6.1 **Responding to interviews**
Participants may be invited to respond to interviews in Collect, to join sessions, or to interact with canvases in Canvas. Interviews may be configured in different ways depending on the project, but respondents do not log in when answering interviews in Collect.

6.2 **Anonymity by design**
Interviews in Collect are designed to be answered **without login or technical identification of the respondent**. Canucci does not provide or require account-based authentication for respondents in Collect, and does not link responses in Collect to User accounts in the Services.

Respondents may choose to include information about themselves in their answers (for example their name or role), but Canucci has no technical means to verify who wrote a specific response, and does not attempt to do so.

The Customer remains responsible for:

* configuring group sizes, filters and result views in a way that respects anonymity in practice; and
* avoiding the use of the Services in ways that intentionally or unintentionally single out identifiable individuals.

While the Services are designed to support anonymity and aggregation, Canucci cannot guarantee that the identity of a respondent can never be inferred from the content of their responses or from context (for example very small groups or highly specific situations).

6.3 **Access to canvases**

* **Public canvases** can be viewed without logging in. Customers are responsible for ensuring that public canvases do not contain information that should not be publicly accessible.
* **Protected canvases** require a User account. Customers are responsible for ensuring that only authorized personnel are granted access to protected canvases and related insights.

---

## 7. Data ownership, licenses and reuse

7.1 **Ownership**

* As between the parties, the **Customer owns** its Content, including interview questions, responses and results.
* **Canucci owns** the Services and underlying technology, including software, models, templates, designs, and generic methods for analysis and visualization.

7.2 **License to provide the Services**
The Customer grants Canucci a non-exclusive, worldwide license to host, process, store, display and reproduce Content **only as necessary** to provide, maintain and secure the Services and to comply with law.

7.3 **Use for improvement and aggregated insights**
Subject to any Enterprise Agreement and the Customer’s choices:

* The Customer may allow Canucci to use its Content in anonymised and aggregated form to improve the Services, including algorithms, features and user experience.
* Where Customers grant such permission, Canucci may also derive aggregated patterns and insights (for example typical change blockers in a sector) and use those in research, product development or marketing, provided that no Customer or individual is identifiable.

7.4 **Retention after end of contract**
If agreed with the Customer (for example in an Enterprise Agreement or project documentation), Canucci may retain certain Content after the end of a contract to facilitate future projects. Otherwise, data retention and deletion follow section 10 below.

7.5 **No model training by providers**
Canucci uses third-party AI model providers to process text and other data. Canucci does **not** permit those providers to train their models on Customer Content.

---

## 8. Privacy and confidentiality

8.1 **Privacy Policy**
Canucci’s processing of personal data in connection with the Services is described in the **Privacy Policy for Collective Discovery by Canucci**, as updated from time to time. The Privacy Policy forms part of these Terms.

8.2 **Data roles**
Typically, the Customer is the controller of personal data relating to its Users and respondents, and Canucci acts as a processor under applicable data protection laws. The detailed data protection terms may be set out in a separate data processing agreement with the Customer.

8.3 **Confidentiality**
Canucci will treat Customer Content as confidential and will not disclose it to third parties except:

* to provide, maintain and secure the Services;
* to comply with applicable law or a valid legal process; or
* with the Customer’s explicit consent.

Canucci’s obligation of confidentiality does not apply to information that is or becomes publicly available through no fault of Canucci, or that Canucci lawfully receives from a third party without restriction.

8.4 **Anonymous data and data subject rights**
For data that is collected and stored in a way that does not allow Canucci or the Customer to identify an individual respondent (for example anonymous interview responses in Collect), data subject rights such as access and erasure under applicable data protection law will generally **not apply**, because the data is no longer personal data.

Participants who wish to exercise rights in relation to their identifiable personal data (such as their account details) should contact the relevant Customer (for example their employer or the public authority running the project), which is typically the controller of that data.

---

## 9. Third-party services and AI models

9.1 **Use of model providers**
To power translation, clustering, tagging, summarisation, anonymisation and similar features, the Services use large language models and other AI services provided by third parties (such as OpenAI, Google and Anthropic). Only the necessary text data is sent for processing.

9.2 **No guarantee of correctness**
AI-generated outputs may contain errors, omissions or biased perspectives. The Services are intended as **decision support**, not as instructions or factual statements. Customers and Users remain responsible for reviewing and interpreting the results and for decisions taken based on them.

9.3 **No professional advice**
The Services do not provide legal, HR, medical or other professional advice. Customers should seek appropriate professional advice before relying on insights from the Services in sensitive or high-impact decisions, especially decisions about individual employees.

---

## 10. Security, data retention and deletion

10.1 **Security measures**
Canucci implements technical and organizational measures designed to protect the confidentiality, integrity and availability of the Services and Customer data. These include secure hosting on reputable cloud infrastructure, role-based access control, and encryption of data in transit. More detailed information about security practices may be provided separately to Customers.

10.2 **Incidents**
If Canucci becomes aware of a security incident affecting Customer data, Canucci will take appropriate steps to contain and investigate the incident and, where required by law or contract, inform affected Customers without undue delay and cooperate with them in fulfilling any legal obligations.

10.3 **Retention**

* Account data, interview data and canvases are generally retained for as long as necessary to provide the Services to the Customer and as required by applicable law.
* If a workspace or interview is deleted in the user interface, it may be soft-deleted for a period before permanent deletion, to allow for recovery or audit if needed.
* When a Customer relationship ends, Users’ access is suspended. Content may be retained for a limited period in backup or soft-deleted form until the Customer requests deletion or a retention period agreed with the Customer expires.

10.4 **Deletion and export**

* The Customer may request deletion of its Content. Canucci will delete or anonymise such data within a reasonable time, subject to technical limitations and legal retention obligations.
* Direct self-service export of all data may not be available. Upon the Customer’s reasonable request, Canucci can assist in exporting certain data (for example in CSV or JSON format) and may charge a reasonable fee for such assistance, unless otherwise agreed.

---

## 11. Service changes, availability and support

11.1 **Service changes**
Canucci may improve, modify or discontinue features of the Services from time to time. Where changes materially affect how the Services work for a Customer, Canucci will use reasonable efforts to notify the Customer in advance via the Services or by email.

11.2 **Availability**
Canucci aims to keep the Services available and performant, but does not guarantee uninterrupted or error-free operation. Availability may be affected by maintenance, network issues, infrastructure providers, customer environments (including firewalls and connectivity) and other factors.

If service levels or uptime commitments apply, they will be set out in the relevant Enterprise Agreement or service documentation.

11.3 **Maintenance**
Canucci may perform planned maintenance that temporarily affects the availability of the Services. Canucci strives to schedule such maintenance to minimize impact on active projects and events and may provide notice in advance where feasible.

11.4 **Support**
Support is provided to paying / enterprise Customers via email or other agreed channels. Response times and scope of support may be defined in the Customer’s Enterprise Agreement.

---

## 12. Trials, pilots and beta features

12.1 **Trials and pilots**
Canucci may offer trials or pilot projects, including pro-bono or democracy-related projects with public-sector or non-profit Customers. These Terms apply equally to such use unless otherwise agreed in writing.

12.2 **Beta features**
Canucci may label certain features as alpha, beta, preview or similar. Such features are provided “as is”, may be changed or discontinued at any time, and may be subject to additional terms.

---

## 13. Consultancy and professional services

Canucci may provide consultancy, facilitation or other professional services in connection with the Services. Such services are governed by the applicable Enterprise Agreement, statement of work or similar document agreed with the Customer. These Terms primarily govern the use of the **SaaS platform** itself.

---

## 14. Changes to these Terms

Canucci may update these Terms from time to time. When we make material changes, we will notify Customers by posting the updated Terms within the Services and/or by email. Continued use of the Services after the updated Terms take effect constitutes acceptance of the changes.

If a Customer does not agree to materially revised Terms and has no Enterprise Agreement that governs the issue, the Customer should stop using the Services and may contact Canucci to discuss options.

---

## 15. Governing law and disputes

These Terms and any dispute, controversy or claim arising out of or in connection with them, or the use of the Services, shall be governed by and construed in accordance with **substantive Swedish law**, without regard to its conflict of law principles.

Any such dispute, controversy or claim shall be finally settled by the **courts of Sweden**, with **Stockholm District Court (Stockholms tingsrätt)** as the court of first instance.

If the Customer and Canucci have entered into an Enterprise Agreement that specifies a different dispute resolution mechanism (for example arbitration), that mechanism shall prevail over this clause.

---

## 16. Contact

If you have any questions about these Terms or the Services, please contact:

**Canucci AB**
Box 16066
SE-103 22 Stockholm
Sweden
Email: [${contactEmail()}](mailto:${contactEmail()})
`;
